import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { unwrapApiArray } from "../../shared/utils/apiResponse";
import { handleGetPublicGames } from "../../userDashboard/userControllers/gameController";
import LoadingGrid from "../../utils/loadingGrid";
import { isGameUpcoming } from "../../utils/matchLifecycle";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

interface Game {
    id: string | number;
    title: string;
    description: string;
    price: number;
    date: string;
    start_time: string;
    image?: string;
    type?: string;
    status?: string;
    [key: string]: unknown;
}

const Matches: React.FC = () => {
    const [games, setGames] = useState<Game[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchGames = async () => {
            try {
                const data = await handleGetPublicGames<Game[]>();
                setGames(unwrapApiArray<Game>(data).filter((game) => isGameUpcoming(game)));
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchGames();
    }, []);

    return (
        <>
            <Navbar />
            <div
                style={{
                    backgroundImage: `url(${landingAsset("herobg.svg")})`,
                    backgroundSize: "cover",
                }}
                className="h-[500px] md:h-[400px] relative w-full flex justify-center items-center flex-col "
            >
                <div
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 17.83%)",
                    }}
                    className=" h-full w-full absolute left-0 top-0 text-center flex flex-col items-center justify-center p-4"
                >
                    <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-black font-Manrope uppercase tracking-tight">Public Matches</h1>
                    <p className=" mt-6 text-center font-Manrope text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">
                        Find the perfect pitch, join a community of players, and elevate your game. The next kick-off is just a click away.
                    </p>
                </div>
            </div>

            <div className="py-24 bg-white px-4 md:px-14">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-10 mb-20">
                        <div className="space-y-4">
                            <h3 className="text-green-700 font-black uppercase tracking-[0.2em] text-sm">Kick Off</h3>
                            <h2 className="text-4xl md:text-5xl font-black text-gray-900 uppercase tracking-tighter">Your Next Match <br /><span className="text-green-700">Starts Here</span></h2>
                        </div>
                        <div className="flex bg-gray-50 p-2 rounded-2xl border border-gray-100">
                            <button className="px-8 py-3 bg-green-700 text-white rounded-xl font-black shadow-lg">All Matches</button>
                            <button className="px-8 py-3 text-gray-400 font-black hover:text-gray-600">Pro Matches</button>
                        </div>
                    </div>

                    {loading ? (
                        <LoadingGrid count={6} />
                    ) : games.length === 0 ? (
                        <div className="bg-gray-50 py-32 rounded-[56px] text-center border-2 border-dashed border-gray-100 italic font-bold text-gray-400 text-2xl">
                            No active matches found at the moment.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                            {games.map((game, i) => (
                                <div key={i} className="group relative bg-gray-50 rounded-[56px] p-10 border border-gray-100 hover:bg-white hover:shadow-2xl transition-all duration-700 overflow-hidden">
                                    <div className="absolute -right-16 -top-16 w-48 h-48 bg-green-50 rounded-full group-hover:scale-150 transition-transform duration-700 pointer-events-none" />

                                    <div className="relative z-10 space-y-8">
                                        <div className="flex justify-between items-center">
                                            <span className="px-5 py-2 bg-green-100 text-green-700 rounded-full font-black text-[10px] uppercase tracking-[0.25em]">
                                                {game.status || 'PUBLIC'}
                                            </span>
                                            <p className="font-black text-2xl text-gray-900 tracking-tighter">₦{game.price || 0}</p>
                                        </div>

                                        <div className="pt-4">
                                            <h3 className="text-3xl font-black text-gray-900 group-hover:text-green-700 transition-colors uppercase tracking-tight leading-none mb-4">{game.title}</h3>
                                            <p className="text-gray-500 font-bold italic line-clamp-2 text-lg leading-relaxed">{game.description}</p>
                                        </div>

                                        <div className="pt-8 space-y-4 border-t border-gray-100">
                                            <div className="flex items-center justify-between text-gray-400 font-black text-sm uppercase tracking-widest">
                                                <div className="flex items-center gap-2">📅 {new Date(game.date).toLocaleDateString()}</div>
                                                <div>🕒 {game.start_time}</div>
                                            </div>
                                            <Link to={`/matches/${game.id}`} className="block w-full py-5 bg-gray-900 group-hover:bg-green-700 text-white rounded-[24px] font-black text-center text-lg shadow-xl active:scale-95 transition-all">View Match</Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Banner />
            <Footer />
        </>
    );
};

export default Matches;
